/**
 * WebSocket transport for one board: connect with the current access token,
 * exponential backoff (0.5 s → 30 s, jittered, forever), immediate retry when
 * the browser comes back online, close-code handling:
 *
 *  - 4001 (bad/expired token) → one awaited refresh, then reconnect at once;
 *    a second 4001 within 30 s (or a failed refresh) → `auth_failed`, stop.
 *  - 4003 (no access to the board) → `no_access`, stop.
 *  - anything else → `reconnecting` with backoff.
 *
 * Every (re)connect gets a fresh `init` from the server — the caller does the
 * full resync there.
 */

import { parseServerMsg, type ClientMsg, type ServerMsg } from './protocol';

export type ConnState = 'connecting' | 'open' | 'reconnecting' | 'no_access' | 'auth_failed' | 'closed';

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export interface BoardSocketOptions {
  url(token: string): string;
  getToken(): string | null;
  refreshToken(): Promise<string | null>;
  onMessage(msg: ServerMsg): void;
  onState?(state: ConnState, detail?: { attempt: number; delayMs?: number; code?: number }): void;
  createSocket?(url: string): SocketLike;
  /** Backoff base (ms). */
  baseDelay?: number;
  maxDelay?: number;
  now?(): number;
  setTimer?(fn: () => void, ms: number): unknown;
  clearTimer?(t: unknown): void;
  random?(): number;
}

const OPEN = 1;

export function backoffDelay(attempt: number, base = 500, max = 30_000, random = Math.random): number {
  const exp = Math.min(max, base * 2 ** Math.max(0, attempt - 1));
  return Math.round(exp * (0.75 + random() * 0.5));
}

export class BoardSocket {
  private ws: SocketLike | null = null;
  private state: ConnState = 'closed';
  private attempt = 0;
  private timer: unknown = null;
  private stopped = true;
  private last4001 = -Infinity;
  private readonly o: Required<Omit<BoardSocketOptions, 'onState'>> & Pick<BoardSocketOptions, 'onState'>;

  constructor(opts: BoardSocketOptions) {
    this.o = {
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      baseDelay: 500,
      maxDelay: 30_000,
      now: () => Date.now(),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
      random: Math.random,
      // Ignore explicitly-undefined options so they don't erase the defaults.
      ...(Object.fromEntries(Object.entries(opts).filter(([, v]) => v !== undefined)) as BoardSocketOptions),
    };
  }

  get connState(): ConnState {
    return this.state;
  }
  get isOpen(): boolean {
    return this.ws?.readyState === OPEN && this.state === 'open';
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimer();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null;
      try {
        ws.close(1000, 'leaving');
      } catch {
        /* ignore */
      }
    }
    this.setState('closed');
  }

  /** Drop the current socket and connect again now (e.g. browser came online). */
  reconnectNow(): void {
    if (this.stopped || this.state === 'no_access' || this.state === 'auth_failed') return;
    this.clearTimer();
    this.dropSocket();
    this.connect();
  }

  /** Force-close the socket as if the network dropped (backoff reconnect follows). */
  simulateDrop(): void {
    if (this.stopped || !this.ws) return;
    this.dropSocket();
    void this.handleClose(1006);
  }

  send(msg: ClientMsg): boolean {
    if (!this.isOpen || !this.ws) return false;
    try {
      this.ws.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }

  private setState(s: ConnState, detail: { attempt: number; delayMs?: number; code?: number } = { attempt: this.attempt }): void {
    this.state = s;
    this.o.onState?.(s, detail);
  }

  private clearTimer(): void {
    if (this.timer != null) this.o.clearTimer(this.timer);
    this.timer = null;
  }

  private dropSocket(): void {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onclose = ws.onmessage = ws.onerror = ws.onopen = null;
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }

  private connect(): void {
    if (this.stopped) return;
    const token = this.o.getToken();
    if (!token) {
      this.setState('auth_failed');
      return;
    }
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    let ws: SocketLike;
    try {
      ws = this.o.createSocket(this.o.url(token));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.setState('open');
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      const msg = parseServerMsg(ev.data);
      if (msg) this.o.onMessage(msg);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      void this.handleClose(ev.code);
    };
  }

  private async handleClose(code: number): Promise<void> {
    if (this.stopped) return;
    if (code === 4003) {
      this.setState('no_access', { attempt: this.attempt, code });
      this.stopped = true;
      return;
    }
    if (code === 4001) {
      const now = this.o.now();
      const recent = now - this.last4001 < 30_000;
      this.last4001 = now;
      const fresh = recent ? null : await this.o.refreshToken();
      if (this.stopped) return;
      if (!fresh) {
        this.setState('auth_failed', { attempt: this.attempt, code });
        this.stopped = true;
        return;
      }
      this.connect();
      return;
    }
    this.scheduleReconnect(code);
  }

  private scheduleReconnect(code?: number): void {
    this.attempt += 1;
    const delay = backoffDelay(this.attempt, this.o.baseDelay, this.o.maxDelay, this.o.random);
    this.setState('reconnecting', { attempt: this.attempt, delayMs: delay, code });
    this.clearTimer();
    this.timer = this.o.setTimer(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }
}
